import { DatePipe } from '@angular/common';
import { buildDeleteConfig } from '../../utils/delete-confirmation';
import { nextDayMidnight } from '../../utils/date-range-filter.utils';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { Subject, takeUntil } from 'rxjs';
import { EntityDialogData } from '../../services/entity-dialog.service';
import {
  DEFAULT_GAP,
  FIXED_LIMITS_DEFAULT_LOWER_BOUND,
  gapValidatorsForType,
  insertCommaForDecimalPoint,
  swapDefaultNegativeGap,
} from '../../utils/checker-gaps';
import { FormulaFormHelperService } from '../../services/formula-load-save-form-helper.service';
import { FormulaFormProvider } from '../../services/formula-model-dialog.service';
import { TestModeService } from '../../services/test-mode.service';
import { EntityFormDeleteAction } from '../../types/entity-form-result.interface';
import { Formula, FormulaModelType, FormulaParameter, FormulaParameters } from '../../types/formula.interface';
import { CheckerRequiredOptions, CheckerType, ISODateTime, MeasureChecker } from '../../types/measure-checker.interface';
import { Measure } from '../../types/measure.interface';
import { Unit } from '../../types/unit.interface';
import { CesDeleteButtonComponent } from '../ui/ces-buttons';
import { CesFormulaModelButtonsComponent } from '../ui/ces-buttons/ces-formula-model-buttons.component';
import { CesDialogFooterComponent } from '../ui/dialog-footer/dialog-footer.component';
import { CesSelectFieldComponent } from '../ui/ces-select-field/ces-select-field.component';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';
import { CesDatepickerComponent } from '../ui/inputs/datepicker/datepicker.component';
import { EditorComponent } from '../ui/inputs/editor/editor.component';
import { EditorVariable } from '../ui/inputs/editor/editor.types';
import { ParameterMode } from '../ui/inputs/parameters-input/parameter-types';
import { ParametersComponent } from '../ui/inputs/parameters-input/parameters.component';

interface MeasureCheckerFormControls {
  type: FormControl<CheckerType>;
  startTs: FormControl<string | null>;
  endTs: FormControl<string | null>;
  comment: FormControl<string>;
  negativeGap: FormControl<number | null>;
  positiveGap: FormControl<number | null>;
  formulaFirstParameters: FormControl<FormulaParameters>;
  formulaFirstExpression: FormControl<string>;
  formulaSecondParameters: FormControl<FormulaParameters>;
  formulaSecondExpression: FormControl<string>;
  formulaFirst: FormControl<Formula | null>;
  formulaSecond: FormControl<Formula | null>;
}

interface MeasureCheckerDialogAdditionalData {
  measureId?: number;
  units?: Unit[];
  measures?: Measure[];
  checkers?: MeasureChecker[];
  selectedMeasureChecker?: MeasureChecker;
}

interface MeasureCheckerDialogData extends Omit<EntityDialogData<MeasureChecker>, 'additionalData'> {
  additionalData?: MeasureCheckerDialogAdditionalData;
}

@Component({
  selector: 'app-measure-checker-form',
  standalone: true,
  imports: [
    ButtonModule,
    CesDatepickerComponent,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    CesFormulaModelButtonsComponent,
    CesSelectFieldComponent,
    CesTextFieldComponent,
    DatePipe,
    EditorComponent,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    ParametersComponent,
    ReactiveFormsModule,
    SelectModule,
    TabsModule,
    TextareaModule,
    TranslocoModule,
  ],
  templateUrl: './measure-checker-form.component.html',
  styleUrl: './measure-checker-form.component.scss',
})
export class MeasureCheckerFormComponent implements OnInit, OnDestroy, AfterViewInit {
  get CheckerOptions(): { name: string; value: CheckerType }[] {
    return CheckerRequiredOptions.map(opt => ({
      name: this.translateService.translate(opt.labelKey),
      value: opt.value,
    }));
  }

  @Input() entity?: MeasureChecker;
  @Input() measureId!: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() units: Unit[] = [];
  @Input() measures: Measure[] = [];

  @Output() formSubmit = new EventEmitter<MeasureChecker>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<MeasureChecker>();

  @ViewChild('formContainer') formContainer!: ElementRef<HTMLElement>;

  readonly ParameterMode = ParameterMode;
  readonly CheckerType = CheckerType;

  form!: FormGroup<MeasureCheckerFormControls>;
  formulaFirstControl!: FormControl<Formula | null>;
  formulaSecondControl!: FormControl<Formula | null>;
  firstEditorVariables: EditorVariable[] = [];
  secondEditorVariables: EditorVariable[] = [];
  submitting = false;
  errorMessage = '';

  polynomialsActiveTab: string = 'tab1';
  polynomialAndGapActiveTab: string = 'poly';

  private readonly fb = inject(FormBuilder);
  private readonly config = inject<DynamicDialogConfig<MeasureCheckerDialogData> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<MeasureCheckerFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });
  protected readonly t = inject(TestModeService);
  private readonly destroy$ = new Subject<void>();

  private directDeleteCallback?: (entity: MeasureChecker) => void;

  private readonly helper = inject(FormulaFormHelperService);
  private readonly translateService = inject(TranslocoService);
  private firstPolynomialProvider!: FormulaFormProvider;
  private secondPolynomialProvider!: FormulaFormProvider;

  ngOnInit(): void {
    const data = this.config?.data;
    if (data) {
      const additional = data.additionalData ?? {};
      if (data.entity !== undefined) this.entity = data.entity;
      this.measureId = additional.measureId ?? this.measureId;
      this.mode = data.mode === 'edit' ? 'edit' : 'create';
      this.units = additional.units ?? [];
      this.measures = additional.measures ?? [];
      if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
    }

    this.initForm();

    const providedEntity = this.entity ?? this.config?.data?.additionalData?.selectedMeasureChecker;
    if (providedEntity) {
      this.patchFormWithEntity(providedEntity);
    }

    this.form
      .get('type')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        if (type !== null && type !== undefined) {
          this.updateFormValidators(type);
          swapDefaultNegativeGap(this.form.get('negativeGap'), type);
        }
        this.polynomialsActiveTab = 'tab1';
        this.polynomialAndGapActiveTab = 'poly';
      });

    this.firstPolynomialProvider = this.helper.createProvider(
      this.form,
      {
        defaultName: this.translateService.translate('forms.lowerPolynomial'),
        expressionField: 'formulaFirstExpression',
        parametersField: 'formulaFirstParameters',
      },
      params => (this.firstEditorVariables = this.helper.parametersToEditorVariables(params)),
    );
    this.secondPolynomialProvider = this.helper.createProvider(
      this.form,
      {
        defaultName: this.translateService.translate('forms.upperPolynomial'),
        expressionField: 'formulaSecondExpression',
        parametersField: 'formulaSecondParameters',
      },
      params => (this.secondEditorVariables = this.helper.parametersToEditorVariables(params)),
    );
  }

  private updateFormValidators(type: CheckerType): void {
    this.form.get('formulaFirstExpression')?.clearValidators();
    this.form.get('formulaSecondExpression')?.clearValidators();

    const needsFirstFormula = type === CheckerType.TWO_POLYNOMIALS || type === CheckerType.POLYNOMIAL_AND_RANGES;
    const needsSecondFormula = type === CheckerType.TWO_POLYNOMIALS;

    this.form.get('negativeGap')?.setValidators(gapValidatorsForType(type));
    this.form.get('positiveGap')?.setValidators(gapValidatorsForType(type));
    // CES-722 : un polynôme peut se passer de paramètres, par exemple une saisonnalité
    // qui ne dépend que de la date. Seule l'expression est exigée.
    if (needsFirstFormula) {
      this.form.get('formulaFirstExpression')?.setValidators(Validators.required);
    }
    if (needsSecondFormula) {
      this.form.get('formulaSecondExpression')?.setValidators(Validators.required);
    }

    this.form.get('negativeGap')?.updateValueAndValidity();
    this.form.get('positiveGap')?.updateValueAndValidity();
    this.form.get('formulaFirstExpression')?.updateValueAndValidity();
    this.form.get('formulaSecondExpression')?.updateValueAndValidity();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.formContainer) {
        this.formContainer.nativeElement.focus();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isValidDateValidator(control: AbstractControl): ValidationErrors | null {
    const date: unknown = control.value;
    if (!date) return { required: true };

    if (date instanceof Date) {
      return isNaN(date.getTime()) ? { invalidDate: true } : null;
    }

    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      return isNaN(parsedDate.getTime()) ? { invalidDate: true } : null;
    }

    return { invalidDate: true };
  }

  private createUniqueDateValidator(): ValidatorFn {
    const checkers = this.config?.data?.additionalData?.checkers ?? null;

    return (control: AbstractControl): ValidationErrors | null => {
      const value: unknown = control.value;
      if (!value || !checkers) {
        return null;
      }

      const currentDate =
        value instanceof Date ? value : typeof value === 'string' || typeof value === 'number' ? new Date(value) : new Date(NaN);

      if (isNaN(currentDate.getTime())) {
        return null;
      }

      const areDatesEqualToSecond = (date1: Date, date2: Date): boolean => {
        return Math.floor(date1.getTime() / 1000) === Math.floor(date2.getTime() / 1000);
      };

      const hasOverlap = checkers.some(checker => {
        if (checker.measureId !== this.measureId) {
          return false;
        }

        if (checker.id === this.entity?.id) {
          return false;
        }

        const startTs = new Date(checker.startTs);
        return areDatesEqualToSecond(startTs, currentDate);
      });

      return hasOverlap ? { uniqueDate: true } : null;
    };
  }

  // Comme le formulaire de formule : ancre le début de validité d'un nouveau checker sur la prochaine frontière de jour à minuit.
  private computeDefaultStartTs(): Date {
    const baseline = nextDayMidnight();
    const checkers: MeasureChecker[] | undefined = this.config?.data?.additionalData?.checkers;
    if (!checkers) return baseline;
    const lastLiveStartMs = checkers
      .filter(c => c.measureId === this.measureId && c.id !== this.entity?.id)
      .map(c => new Date(c.startTs).getTime())
      .reduce((max, ms) => (ms > max ? ms : max), 0);
    if (lastLiveStartMs <= 0) return baseline;
    const lastNext = nextDayMidnight(new Date(lastLiveStartMs));
    return lastNext.getTime() > baseline.getTime() ? lastNext : baseline;
  }

  private initForm(): void {
    this.formulaFirstControl = this.fb.control<Formula | null>(null);
    this.formulaSecondControl = this.fb.control<Formula | null>(null);

    this.form = new FormGroup<MeasureCheckerFormControls>({
      type: this.fb.nonNullable.control(CheckerType.TWO_FIXED_LIMITS, [Validators.required]),
      startTs: this.fb.control<string | null>(this.computeDefaultStartTs().toISOString(), [
        Validators.required,
        this.isValidDateValidator,
        this.createUniqueDateValidator(),
      ]),
      endTs: this.fb.control<string | null>(null),
      comment: this.fb.nonNullable.control(''),
      negativeGap: this.fb.control<number | null>(FIXED_LIMITS_DEFAULT_LOWER_BOUND),
      positiveGap: this.fb.control<number | null>(DEFAULT_GAP),
      formulaFirstParameters: this.fb.nonNullable.control<FormulaParameters>([]),
      formulaFirstExpression: this.fb.nonNullable.control(''),
      formulaSecondParameters: this.fb.nonNullable.control<FormulaParameters>([]),
      formulaSecondExpression: this.fb.nonNullable.control(''),
      formulaFirst: this.formulaFirstControl,
      formulaSecond: this.formulaSecondControl,
    });

    this.updateFormValidators(CheckerType.TWO_FIXED_LIMITS);
  }

  private patchFormWithEntity(entity: MeasureChecker): void {
    const startDate = entity.startTs ?? new Date().toISOString();
    const endDate = entity.endTs ?? null;

    this.form.patchValue({
      type: entity.type,
      startTs: startDate,
      endTs: endDate,
      comment: entity.comment,
      negativeGap: entity.negativeGap ?? null,
      positiveGap: entity.positiveGap ?? null,
    });

    if (entity.formulaFirst) {
      this.onFirstParametersChange(entity.formulaFirst.parameters ?? []);
      this.form.patchValue({
        formulaFirstParameters: entity.formulaFirst.parameters ?? [],
        formulaFirstExpression: entity.formulaFirst.expression ?? '',
        formulaFirst: entity.formulaFirst,
      });
    }

    if (entity.formulaSecond) {
      this.onSecondParametersChange(entity.formulaSecond.parameters ?? []);
      this.form.patchValue({
        formulaSecondParameters: entity.formulaSecond.parameters ?? [],
        formulaSecondExpression: entity.formulaSecond.expression ?? '',
        formulaSecond: entity.formulaSecond,
      });
    }

    this.updateFormValidators(entity.type);
    const startTsControl = this.form.get('startTs');
    startTsControl?.markAsTouched();
    startTsControl?.updateValueAndValidity();
  }

  onFirstParametersChange(parameters: FormulaParameter[]): void {
    this.firstEditorVariables = this.helper.parametersToEditorVariables(parameters);
  }

  onSecondParametersChange(parameters: FormulaParameter[]): void {
    this.secondEditorVariables = this.helper.parametersToEditorVariables(parameters);
  }

  onDecimalKeyDown(event: KeyboardEvent): void {
    insertCommaForDecimalPoint(event);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field?.errors) return '';

    if (field.errors['required']) return this.translateService.translate('validation.required');
    const minLengthError = field.errors['minlength'] as { requiredLength?: number } | undefined;
    if (minLengthError) return this.translateService.translate('validation.minLength', { min: minLengthError.requiredLength });
    const maxLengthError = field.errors['maxlength'] as { requiredLength?: number } | undefined;
    if (maxLengthError) return this.translateService.translate('validation.maxLength', { max: maxLengthError.requiredLength });
    const minError = field.errors['min'] as { min?: number } | undefined;
    if (minError) return this.translateService.translate('validation.min', { min: minError.min });
    if (field.errors['invalidDate']) return this.translateService.translate('validation.date.invalid');
    if (field.errors['uniqueDate']) return this.translateService.translate('validation.date.uniqueChecker');

    return this.translateService.translate('validation.invalidField');
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      this.errorMessage = this.translateService.translate('validation.formHasErrors');
      return;
    }

    const formValue = this.form.getRawValue();

    this.submitting = true;
    this.errorMessage = '';

    let formulaFirst: Formula | undefined;
    let formulaSecond: Formula | undefined;

    if (formValue.type === CheckerType.TWO_POLYNOMIALS || formValue.type === CheckerType.POLYNOMIAL_AND_RANGES) {
      formulaFirst = {
        id: this.entity?.formulaFirst?.id ?? 0,
        name: this.translateService.translate('forms.lowerPolynomial'),
        expression: formValue.formulaFirstExpression,
        comment: '',
        parameters: formValue.formulaFirstParameters,
      };
    }

    if (formValue.type === CheckerType.TWO_POLYNOMIALS) {
      formulaSecond = {
        id: this.entity?.formulaSecond?.id ?? 0,
        name: this.translateService.translate('forms.upperPolynomial'),
        expression: formValue.formulaSecondExpression,
        comment: '',
        parameters: formValue.formulaSecondParameters,
      };
    }

    const startTs: ISODateTime = formValue.startTs ?? new Date().toISOString();
    const endTs: ISODateTime | undefined = formValue.endTs ?? undefined;

    const includeGaps = formValue.type === CheckerType.TWO_FIXED_LIMITS || formValue.type === CheckerType.POLYNOMIAL_AND_RANGES;

    const measureChecker: MeasureChecker = {
      id: this.entity?.id ?? 0,
      measureId: this.measureId,
      type: formValue.type,
      startTs,
      comment: formValue.comment,
      formulaIdFirst: this.entity?.formulaIdFirst ?? 0,
      name: 'Checker',
      ...(endTs !== undefined && { endTs }),
      ...(includeGaps && formValue.negativeGap !== null ? { negativeGap: formValue.negativeGap } : {}),
      ...(includeGaps && formValue.positiveGap !== null ? { positiveGap: formValue.positiveGap } : {}),
      ...(this.entity?.formulaIdSecond !== undefined && { formulaIdSecond: this.entity.formulaIdSecond }),
      ...(formulaFirst !== undefined && { formulaFirst }),
      ...(formulaSecond !== undefined && { formulaSecond }),
    };

    if (this.dialogRef) {
      this.dialogRef.close(measureChecker);
    } else {
      this.formSubmit.emit(measureChecker);
    }

    this.submitting = false;
  }

  onCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDelete(): void {
    if (!this.entity) return;

    if (this.directDeleteCallback) {
      this.directDeleteCallback(this.entity);
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    } else if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<MeasureChecker> = { __action: 'delete', entity: this.entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(this.entity);
    }
  }

  getDeleteConfirmationConfig() {
    return buildDeleteConfig('entities.measureChecker.messages.deleteConfirm', this.entity?.name ?? '');
  }

  private get formulaDialogOptions(): { measureId: number; units: Unit[]; measures: Measure[]; modelType: FormulaModelType } {
    return {
      measureId: this.measureId,
      units: this.units,
      measures: this.measures,
      modelType: FormulaModelType.CHECKER,
    };
  }

  openLoadFirstPolynomialModelDialog(): void {
    this.helper.openLoadDialog(this.firstPolynomialProvider, this.formulaDialogOptions);
  }

  openSaveFirstPolynomialModelDialog(): void {
    this.helper.openSaveDialog(this.firstPolynomialProvider, this.formulaDialogOptions);
  }

  openLoadSecondPolynomialModelDialog(): void {
    this.helper.openLoadDialog(this.secondPolynomialProvider, this.formulaDialogOptions);
  }

  openSaveSecondPolynomialModelDialog(): void {
    this.helper.openSaveDialog(this.secondPolynomialProvider, this.formulaDialogOptions);
  }
}
